import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Stethoscope, GraduationCap, Award, Linkedin, Mail, ArrowRight, Quote } from 'lucide-react';
import { EnhancedScrollReveal } from '../common/EnhancedScrollReveal';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
const experts = [{
  id: 1,
  name: 'Dr. Roberto Méndez',
  role: 'Director Veterinario',
  specialty: 'Nutrición Clínica',
  image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?auto=format&fit=crop&q=80&w=800',
  bio: 'Más de 15 años liderando equipos de nutrición clínica. Su investigación sobre dietas antiinflamatorias ha sido publicada en revistas internacionales.',
  quote: 'La nutrición no es solo combustible, es la medicina preventiva más potente que existe.',
  credentials: ['PhD Nutrición Animal', 'WSAVA Member', 'Profesor UCM']
}, {
  id: 2,
  name: 'Dra. Elena Torres',
  role: 'I+D+i Alimentaria',
  specialty: 'Tecnología de Alimentos',
  image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&q=80&w=800',
  bio: 'Pionera en técnicas de cocción a baja temperatura para mascotas. Asegura que cada receta mantenga su integridad nutricional.',
  quote: 'Buscamos el equilibrio perfecto entre seguridad alimentaria y frescura natural.',
  credentials: ['Máster Seguridad Alimentaria', 'Certificación IFS']
}, {
  id: 3,
  name: 'Dr. Javier Soto',
  role: 'Etología Clínica',
  specialty: 'Comportamiento',
  image: 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=crop&q=80&w=800',
  bio: 'Integra la salud mental en la ecuación del bienestar. Diseña nuestros protocolos de enriquecimiento ambiental.',
  quote: 'Un perro sano necesita nutrir su mente tanto como su cuerpo.',
  credentials: ['Especialista Etología', 'Autor Publicado']
}];
export function ExpertTeam() {
  const [hoveredExpert, setHoveredExpert] = useState<number | null>(null);
  return <section className="py-24 lg:py-32 bg-[#FAF9F6]" data-id="element-1890">
      <div className="container-custom max-w-7xl mx-auto" data-id="element-1891">
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8" data-id="element-1892">
          <div className="max-w-2xl" data-id="element-1893">
            <EnhancedScrollReveal data-id="element-1894">
              <Badge variant="secondary" className="mb-6 bg-blue-100 text-blue-700 border-blue-200" data-id="element-1895">
                <Stethoscope className="w-3 h-3 mr-1" data-id="element-1896" /> Comité Científico
              </Badge>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-charcoal-900 mb-6" data-id="element-1897">
                Ciencia, no opiniones
              </h2>
              <p className="text-lg text-charcoal-600 leading-relaxed font-light" data-id="element-1898">
                Cada receta, cada juguete y cada recomendación está respaldada
                por nuestro equipo de especialistas veterinarios.
              </p>
            </EnhancedScrollReveal>
          </div>

          <EnhancedScrollReveal delay={0.2} data-id="element-1899">
            <Button variant="outline" className="rounded-full border-charcoal-200 hover:bg-white" data-id="element-1900">
              Conocer al equipo completo <ArrowRight className="ml-2 w-4 h-4" data-id="element-1901" />
            </Button>
          </EnhancedScrollReveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20" data-id="element-1902">
          {experts.map((expert, index) => <EnhancedScrollReveal key={expert.id} delay={index * 0.1} data-id="element-1903">
              <div className="group relative h-[500px] rounded-[2rem] overflow-hidden cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-500" onMouseEnter={() => setHoveredExpert(expert.id)} onMouseLeave={() => setHoveredExpert(null)} data-id="element-1904">
                {/* Background Image */}
                <img src={expert.image} alt={expert.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale group-hover:grayscale-0" data-id="element-1905" />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal-900 via-charcoal-900/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" data-id="element-1906" />

                {/* Content */}
                <div className="absolute inset-0 p-8 flex flex-col justify-end text-white" data-id="element-1907">
                  <div className="transform transition-transform duration-500 translate-y-4 group-hover:translate-y-0" data-id="element-1908">
                    <div className="text-primary-400 font-bold text-sm uppercase tracking-wider mb-2" data-id="element-1909">
                      {expert.role}
                    </div>
                    <h3 className="text-3xl font-serif font-bold mb-4" data-id="element-1910">
                      {expert.name}
                    </h3>

                    {/* Expandable Content */}
                    <div className={`overflow-hidden transition-all duration-500 ${hoveredExpert === expert.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-1911">
                      <p className="text-gray-300 text-sm leading-relaxed mb-6 border-l-2 border-primary-500 pl-4 italic" data-id="element-1912">
                        "{expert.quote}"
                      </p>

                      <div className="flex flex-wrap gap-2 mb-6" data-id="element-1913">
                        {expert.credentials.map((cred, i) => <span key={i} className="px-2 py-1 bg-white/10 rounded-md text-[10px] font-bold uppercase tracking-wide text-white border border-white/10" data-id="element-1914">
                            {cred}
                          </span>)}
                      </div>

                      <Button size="sm" variant="white" className="rounded-full w-full" data-id="element-1915">
                        Ver Publicaciones
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </EnhancedScrollReveal>)}
        </div>

        {/* CTA Section */}
        <EnhancedScrollReveal delay={0.4} data-id="element-1916">
          <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-xl border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-1917">
            <div className="flex items-center gap-6" data-id="element-1918">
              <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 shrink-0" data-id="element-1919">
                <Mail className="w-8 h-8" data-id="element-1920" />
              </div>
              <div data-id="element-1921">
                <h3 className="text-2xl font-bold text-charcoal-900 mb-1" data-id="element-1922">
                  ¿Dudas veterinarias?
                </h3>
                <p className="text-charcoal-600" data-id="element-1923">
                  Nuestro equipo responde a tus consultas sobre nutrición y
                  salud en 24h.
                </p>
              </div>
            </div>
            <Button size="lg" className="rounded-full px-8 shadow-lg shadow-primary-900/10 whitespace-nowrap" data-id="element-1924">
              Contactar con Expertos
            </Button>
          </div>
        </EnhancedScrollReveal>
      </div>
    </section>;
}